import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cxmService,
  CxmMethod,
  CxmPayoutAccount,
  CxmPayoutAccountRequirements,
  methodLimits,
  validateAmount,
  validatePayoutDetails,
  visibleFields,
} from "src/api";
import { useDepositWithdrawContext, useUrlParamsContext } from "src/contexts";
import { formatNumberToExchange, onlyNumbers } from "src/utils";

/**
 * CXM payout orchestrator. Mirrors `useAlfredWithdrawal` in shape, with the
 * differences that CXM's own behaviour forces:
 *
 *  - limits come from `catalog.methods` and are enforced here, because CXM's
 *    quote endpoint answers 200 for amounts outside them;
 *  - payout-account fields come from the live schema, since their names are
 *    also the JSON keys and CXM validates none of them;
 *  - the outcome of a payout is not predictable from the amount, so nothing
 *    is promised until the backend answers.
 */
export const useCxmWithdrawal = () => {
  const { balance } = useDepositWithdrawContext();
  const { paymentRouteId } = useUrlParamsContext();
  const { currency } = balance;

  const [methods, setMethods] = useState<CxmMethod[]>([]);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [methodsError, setMethodsError] = useState<string | null>(null);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<CxmPayoutAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);

  const [requirements, setRequirements] = useState<CxmPayoutAccountRequirements | null>(null);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsError, setRequirementsError] = useState<string | null>(null);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [addAccountLoading, setAddAccountLoading] = useState(false);
  const [addAccountError, setAddAccountError] = useState<string | null>(null);

  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ amount: string; status: string } | null>(null);

  const selectedMethod = useMemo(() => methods.find((m) => m.methodId === selectedMethodId) ?? null, [methods, selectedMethodId]);

  /** An empty catalogue is a legitimate state: the operator granted no rules. */
  useEffect(() => {
    let cancelled = false;
    setMethodsLoading(true);
    cxmService
      .getMethods("withdrawal")
      .then((list) => {
        if (cancelled) return;
        setMethods(list);
        setSelectedMethodId(list[0]?.methodId ?? null);
        setMethodsError(null);
      })
      .catch((e: Error) => !cancelled && setMethodsError(e.message))
      .finally(() => !cancelled && setMethodsLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAccounts = useCallback(async (typeId?: number) => {
    setAccountsLoading(true);
    try {
      const list = await cxmService.listPayoutAccounts();
      setAccounts(list);
      // Accounts are typed: a bank account cannot be used for a USDT method.
      const usable = typeId ? list.filter((a) => a.paymentMethodTypeId === typeId) : list;
      setSelectedAccountId((current) => (usable.some((a) => a.id === current) ? current : (usable[0]?.id ?? null)));
    } catch {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    const typeId = selectedMethod?.paymentMethodTypeId;
    if (!typeId) return;
    setRequirements(null);
    setRequirementsError(null);
    setRequirementsLoading(true);
    loadAccounts(typeId);
    // The failure has to be visible. Swallowed, it left the form with an
    // empty field list, and the only route to adding a payout account is
    // built from that list — so the button quietly disabled itself and the
    // flow looked like a missing feature rather than a failed request.
    cxmService
      .getPayoutAccountRequirements(typeId)
      .then((r) => {
        setRequirements(r);
        setRequirementsError(null);
      })
      .catch((e: Error) => {
        setRequirements(null);
        setRequirementsError(e.message);
      })
      .finally(() => setRequirementsLoading(false));
  }, [selectedMethod?.paymentMethodTypeId, loadAccounts]);

  const usableAccounts = useMemo(
    () =>
      selectedMethod?.paymentMethodTypeId
        ? accounts.filter((a) => a.paymentMethodTypeId === selectedMethod.paymentMethodTypeId)
        : [],
    [accounts, selectedMethod?.paymentMethodTypeId]
  );

  /**
   * Method limits first, then the balance.
   *
   * The balance check is here rather than in `validateAmount` because that
   * function only knows the method. Without it the form let the user fill in
   * an amount they plainly did not have — the panel says `Available 0.00` two
   * lines above the button — and the refusal came back from the server as
   * `You have insufficient balance to withdraw (77E0A8A1)`, a support code for
   * something the page already knew.
   */
  const amountError = useMemo(() => {
    if (!selectedMethod) return undefined;

    const limitError = validateAmount(selectedMethod, amount || "0");
    if (limitError) return limitError;

    const value = Number(amount);
    const free = Number(balance.free_balance) || 0;
    if (Number.isFinite(value) && value > free) {
      return `You have ${formatNumberToExchange(
        String(free),
        Math.max(currency.precision || 0, 2)
      )} ${currency.currency_id} available.`;
    }

    return undefined;
  }, [selectedMethod, amount, balance.free_balance, currency.precision, currency.currency_id]);

  const limits = useMemo(() => (selectedMethod ? methodLimits(selectedMethod) : { min: null, max: null }), [selectedMethod]);

  /**
   * `onlyNumbers` is a predicate, not a formatter — it gates the keystroke.
   * Precision is clamped to at least 2 so a currency configured with 0
   * decimals does not floor "40.50" to "40".
   */
  const handleAmountChange = useCallback(
    (value: string) => {
      setAmountTouched(true);
      if (!onlyNumbers(value)) return;
      const decimals = Math.max(currency.precision || 0, 2);
      setAmount(formatNumberToExchange(value, decimals));
    },
    [currency.precision]
  );

  /**
   * Everything the user could actually send: their balance, held under the
   * method's ceiling. Capping here rather than letting the amount fail
   * validation is the point — "max" that produces an error is not a max.
   */
  const maxSendable = useMemo(() => {
    const free = Number(balance.free_balance) || 0;
    return limits.max === null ? free : Math.min(free, limits.max);
  }, [balance.free_balance, limits.max]);

  const handleMaxAmount = useCallback(() => {
    setAmountTouched(true);
    const decimals = Math.max(currency.precision || 0, 2);
    // Truncate rather than round: rounding up invents balance the user
    // does not have, and the gateway would reject it.
    const factor = 10 ** decimals;
    const truncated = Math.floor(maxSendable * factor) / factor;
    setAmount(formatNumberToExchange(String(truncated), decimals));
  }, [maxSendable, currency.precision]);

  const handleAddAccount = useCallback(
    async (details: Record<string, string>) => {
      const typeId = selectedMethod?.paymentMethodTypeId;
      if (!typeId || !requirements) return;

      // CXM stores unknown or blank keys as `approved`, so this is the last
      // point where a bad payout account can still be stopped.
      const fieldErrors = validatePayoutDetails(requirements, details);
      if (Object.keys(fieldErrors).length) {
        setAddAccountError(Object.values(fieldErrors)[0]);
        return;
      }

      setAddAccountLoading(true);
      setAddAccountError(null);
      try {
        const created = await cxmService.createPayoutAccount({
          paymentMethodTypeId: typeId,
          details,
        });
        await loadAccounts(typeId);
        setSelectedAccountId(created.id);
        setAddAccountOpen(false);
      } catch (e) {
        setAddAccountError((e as Error).message);
      } finally {
        setAddAccountLoading(false);
      }
    },
    [selectedMethod?.paymentMethodTypeId, requirements, loadAccounts]
  );

  const submitDisabled = submitLoading || !selectedMethod || !selectedAccountId || !amount || Boolean(amountError);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!selectedMethod || !selectedAccountId || !paymentRouteId) return;

      setSubmitLoading(true);
      setSubmitError(null);
      try {
        const res = await cxmService.initiateWithdrawal({
          payment_route_id: paymentRouteId,
          amount,
          methodId: selectedMethod.methodId,
          paymentMethodId: selectedAccountId,
        });
        setSubmitted({ amount, status: res.status });
      } catch (e) {
        setSubmitError((e as Error).message);
      } finally {
        setSubmitLoading(false);
      }
    },
    [selectedMethod, selectedAccountId, paymentRouteId, amount]
  );

  const reset = useCallback(() => {
    setSubmitted(null);
    setAmount("");
    setAmountTouched(false);
    setSubmitError(null);
  }, []);

  return {
    currency,
    balance: balance.free_balance,
    methods,
    methodsLoading,
    methodsError,
    selectedMethod,
    selectedMethodId,
    setSelectedMethodId,

    accounts: usableAccounts,
    accountsLoading,
    selectedAccountId,
    setSelectedAccountId,
    requirements,
    requirementFields: requirements ? visibleFields(requirements) : [],
    requirementsLoading,
    requirementsError,

    addAccountOpen,
    setAddAccountOpen,
    addAccountLoading,
    addAccountError,
    handleAddAccount,

    amount,
    amountTouched,
    amountError,
    limits,
    maxSendable,
    handleAmountChange,
    handleMaxAmount,

    submitDisabled,
    submitLoading,
    submitError,
    submitted,
    handleSubmit,
    reset,
  };
};

/** The orchestrator's state, so the page can own the hook and pass it down. */
export type CxmWithdrawalState = ReturnType<typeof useCxmWithdrawal>;
